import { Suspense, lazy, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { MessageCircleQuestion } from "lucide-react";

import { useLocale } from "@/i18n/context";

// Loaded on first click: the form pulls in zod (~55 KB minified), which the
// landing page otherwise never needs.
const ContactModal = lazy(() =>
  import("@/components/ContactModal").then((m) => ({ default: m.ContactModal }))
);

export const FAQ = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  // Stays mounted after the first open so a half-typed message survives closing.
  const [contactRequested, setContactRequested] = useState(false);
  const { t } = useLocale();

  return (
    <section className="faq-section">
      <div className="container mx-auto px-5 sm:px-8">
        <div className="faq-layout">
          <div>
            <p className="editorial-eyebrow">{t.faq.eyebrow}</p>
            <h2 className="section-title mt-4">{t.faq.title}</h2>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{t.faq.description}</p>
            <div className="faq-contact">
              <p className="font-semibold text-sm">{t.faq.contactTitle}</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t.faq.contactDesc}</p>
              <Button variant="outline" className="mt-4 gap-2" onClick={() => { setContactRequested(true); setShowContactModal(true); }}>
                <MessageCircleQuestion className="h-4 w-4" />{t.faq.contactCta}
              </Button>
            </div>
          </div>
          <Accordion type="single" collapsible className="faq-questions">
            {t.faq.items.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="gap-4 py-6 text-left text-sm font-medium hover:no-underline hover:text-primary">
                  <span className="flex items-start gap-4"><span aria-hidden="true" className="mt-0.5 text-[10px] tabular-nums text-muted-foreground">{String(index + 1).padStart(2, "0")}</span>{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="pl-8 text-sm leading-relaxed text-muted-foreground">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {contactRequested && (
        <Suspense fallback={null}>
          <ContactModal
            isOpen={showContactModal}
            onClose={() => setShowContactModal(false)}
          />
        </Suspense>
      )}
    </section>
  );
};
